import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Form,
    Button,
    Row,
    Col,
    Alert,
    Spinner,
    Badge,
    ProgressBar,
    ListGroup
} from "react-bootstrap";
import {
    FaFileAlt,
    FaPaperclip,
    FaTrash,
    FaCalendarAlt,
    FaClipboardList,
    FaExclamationTriangle,
    FaCheckCircle
} from "react-icons/fa";
import {
    getFirestore,
    collection,
    addDoc,
    serverTimestamp,
    doc,
    getDoc
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import firebaseApp from "../../firebase/credenciales";
import { notificarNuevaSolicitud } from "../../services/notificacionesService";
import { logBusinessAction } from "../../services/AuditService";
import Swal from "sweetalert2";

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

// Tipos de ausencia
const TIPOS_AUSENCIA = [
    {
        value: "DescansoMedico",
        label: "Descanso Médico",
        color: "#EF4444",
        icon: "🏥",
        requiereDocumento: true
    },
    {
        value: "Permisos",
        label: "Permiso Personal",
        color: "#F59E0B",
        icon: "⏰",
        requiereDocumento: false
    },
    {
        value: "Paternidad",
        label: "Licencia por Paternidad",
        color: "#3B82F6",
        icon: "👨‍👦",
        requiereDocumento: true
    },
    {
        value: "Maternidad",
        label: "Licencia por Maternidad",
        color: "#EC4899",
        icon: "👶",
        requiereDocumento: true
    },
    {
        value: "Vacaciones",
        label: "Vacaciones",
        color: "#10B981",
        icon: "🏖️",
        requiereDocumento: false
    },
    {
        value: "DescansoRegimen",
        label: "Descanso por Régimen (Papeleta)",
        color: "#6366F1",
        icon: "📑",
        requiereDocumento: false
    },
    {
        value: "Otros",
        label: "Otros",
        color: "#8B5CF6",
        icon: "📋",
        requiereDocumento: false
    }
];

function SolicitudAusencia({ usuario }) {
    // Estados del formulario
    const [formData, setFormData] = useState({
        tipo: "",
        fechaInicio: "",
        fechaFin: "",
        motivo: ""
    });

    const [documentos, setDocumentos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [diasCalculados, setDiasCalculados] = useState(0);
    const [errores, setErrores] = useState({});
    const [saldoVacaciones, setSaldoVacaciones] = useState(null);
    const [loadingSaldo, setLoadingSaldo] = useState(false);

    // Obtener saldo de vacaciones cuando se selecciona "Vacaciones"
    useEffect(() => {
        if (formData.tipo === "Vacaciones" && usuario.dni) {
            obtenerSaldoVacaciones();
        }
    }, [formData.tipo, usuario.dni]);

    // Calcular días automáticamente cuando cambian las fechas
    useEffect(() => {
        if (formData.fechaInicio && formData.fechaFin) {
            calcularDias();
        }
    }, [formData.fechaInicio, formData.fechaFin]);

    const obtenerSaldoVacaciones = async () => {
        setLoadingSaldo(true);
        try {
            const vacacionesRef = doc(
                db,
                `vacaciones/${usuario.dni}/vacaciones`,
                "saldo"
            );
            const vacacionesSnap = await getDoc(vacacionesRef);

            if (vacacionesSnap.exists()) {
                const data = vacacionesSnap.data();
                setSaldoVacaciones(data.diasDisponibles || 0);
            } else {
                setSaldoVacaciones(0);
            }
        } catch (error) {
            console.error("Error al obtener saldo de vacaciones:", error);
            setSaldoVacaciones(null);
        } finally {
            setLoadingSaldo(false);
        }
    };

    const calcularDias = () => {
        const inicio = new Date(formData.fechaInicio);
        const fin = new Date(formData.fechaFin);

        if (inicio && fin && inicio <= fin) {
            const diffTime = Math.abs(fin - inicio);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            setDiasCalculados(diffDays);
        } else {
            setDiasCalculados(0);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

        // Limpiar error del campo
        if (errores[name]) {
            setErrores((prev) => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);

        if (files.length === 0) return;

        // Validar tamaño (max 5MB por archivo)
        const maxSize = 5 * 1024 * 1024;
        const archivosValidos = files.filter((file) => {
            if (file.size > maxSize) {
                Swal.fire(
                    "Archivo muy grande",
                    `El archivo ${file.name} excede el límite de 5MB`,
                    "warning"
                );
                return false;
            }
            return true;
        });

        // Agregar archivos al estado
        setDocumentos((prev) => [
            ...prev,
            ...archivosValidos.map((file) => ({
                file,
                nombre: file.name,
                tipo: file.type,
                uploading: false,
                uploaded: false,
                url: null
            }))
        ]);
    };

    const eliminarDocumento = (index) => {
        setDocumentos((prev) => prev.filter((_, i) => i !== index));
    };

    const validarFormulario = () => {
        const nuevosErrores = {};

        if (!formData.tipo) {
            nuevosErrores.tipo = "Debe seleccionar un tipo de ausencia";
        }

        if (!formData.fechaInicio) {
            nuevosErrores.fechaInicio = "Debe seleccionar una fecha de inicio";
        }

        if (!formData.fechaFin) {
            nuevosErrores.fechaFin = "Debe seleccionar una fecha de fin";
        }

        if (formData.fechaInicio && formData.fechaFin) {
            const inicio = new Date(formData.fechaInicio);
            const fin = new Date(formData.fechaFin);

            if (inicio > fin) {
                nuevosErrores.fechaFin =
                    "La fecha de fin debe ser posterior a la fecha de inicio";
            }

            // Validar que no sea en el pasado
            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);
            if (inicio < hoy) {
                nuevosErrores.fechaInicio =
                    "No puede solicitar ausencias con fechas pasadas";
            }
        }

        if (!formData.motivo || formData.motivo.trim().length < 10) {
            nuevosErrores.motivo =
                "Debe ingresar un motivo (mínimo 10 caracteres)";
        }

        // Validar saldo de vacaciones
        if (
            formData.tipo === "Vacaciones" &&
            saldoVacaciones !== null &&
            diasCalculados > saldoVacaciones
        ) {
            nuevosErrores.fechaFin = `No tiene suficientes días disponibles. Saldo: ${saldoVacaciones} días`;
        }

        // Validar documentos requeridos
        const tipoSeleccionado = TIPOS_AUSENCIA.find(
            (t) => t.value === formData.tipo
        );
        if (tipoSeleccionado?.requiereDocumento && documentos.length === 0) {
            nuevosErrores.documentos = `Este tipo de ausencia requiere documentos adjuntos`;
        }

        setErrores(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const subirDocumentos = async () => {
        const documentosSubidos = [];

        for (let i = 0; i < documentos.length; i++) {
            const doc = documentos[i];

            if (doc.uploaded) {
                documentosSubidos.push({
                    nombre: doc.nombre,
                    url: doc.url,
                    tipo: doc.tipo
                });
                continue;
            }

            try {
                // Actualizar estado de subida
                setDocumentos((prev) =>
                    prev.map((d, idx) =>
                        idx === i ? { ...d, uploading: true } : d
                    )
                );

                const ruta = `ausencias/${usuario.ruc || usuario.rucEmpresa}/${usuario.dni
                    }/${Date.now()}_${doc.nombre}`;
                const docRef = ref(storage, ruta);

                await uploadBytes(docRef, doc.file);
                const url = await getDownloadURL(docRef);

                documentosSubidos.push({
                    nombre: doc.nombre,
                    url: url,
                    tipo: doc.tipo
                });

                // Marcar como subido
                setDocumentos((prev) =>
                    prev.map((d, idx) =>
                        idx === i
                            ? { ...d, uploading: false, uploaded: true, url }
                            : d
                    )
                );
            } catch (error) {
                console.error("Error al subir documento:", error);
                throw new Error(`Error al subir ${doc.nombre}`);
            }
        }

        return documentosSubidos;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validarFormulario()) {
            Swal.fire(
                "Formulario incompleto",
                "Por favor complete todos los campos requeridos",
                "warning"
            );
            return;
        }

        const result = await Swal.fire({
            title: "¿Confirmar solicitud?",
            html: `
        <div style="text-align: left;">
          <p><strong>Tipo:</strong> ${TIPOS_AUSENCIA.find((t) => t.value === formData.tipo)?.label
                }</p>
          <p><strong>Desde:</strong> ${new Date(
                    formData.fechaInicio
                ).toLocaleDateString("es-PE")}</p>
          <p><strong>Hasta:</strong> ${new Date(
                    formData.fechaFin
                ).toLocaleDateString("es-PE")}</p>
          <p><strong>Días:</strong> ${diasCalculados}</p>
          <p><strong>Motivo:</strong> ${formData.motivo}</p>
        </div>
      `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sí, enviar solicitud",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#3498db"
        });

        if (!result.isConfirmed) return;

        setLoading(true);
        setUploading(true);

        try {
            // 1. Subir documentos
            const documentosSubidos = await subirDocumentos();

            // 2. Crear solicitud en Firestore
            const solicitudData = {
                rucEmpresa: usuario.ruc || usuario.rucEmpresa,
                empleadoDni: usuario.dni,
                nombreEmpleado: usuario.nombreCompleto || usuario.nombre,
                tipo: formData.tipo,
                fechaInicio: new Date(formData.fechaInicio),
                fechaFin: new Date(formData.fechaFin),
                totalDias: diasCalculados,
                motivo: formData.motivo,
                documentos: documentosSubidos,
                estado: "pendiente",
                createdAt: serverTimestamp()
            };

            const solicitudesRef = collection(db, "solicitudes_ausencia");
            const docRef = await addDoc(solicitudesRef, solicitudData);

            // 3. Notificar a administradores
            await notificarNuevaSolicitud(
                {
                    id: docRef.id,
                    empleadoDNI: usuario.dni,
                    empleadoNombre: usuario.nombreCompleto || usuario.nombre,
                    tipoAusencia: TIPOS_AUSENCIA.find((t) => t.value === formData.tipo)
                        ?.label,
                    fechaInicio: new Date(formData.fechaInicio),
                    fechaFin: new Date(formData.fechaFin)
                },
                usuario.ruc || usuario.rucEmpresa
            );

            // AUDITORÍA
            try {
                await logBusinessAction("CREACION_SOLICITUD_AUSENCIA", "GestionAusencias", {
                    dniEditado: usuario.dni,
                    cambiosRealizados: {
                        solicitud: { anterior: null, nuevo: "Creada" }
                    },
                    tipoAusencia: TIPOS_AUSENCIA.find((t) => t.value === formData.tipo)?.label || formData.tipo,
                    fechaInicio: formData.fechaInicio,
                    fechaFin: formData.fechaFin,
                    diasSolicitados: diasCalculados,
                    idSolicitud: docRef.id
                }, usuario);
            } catch (e) {
                console.warn("Error al registrar auditoría:", e);
            }

            // 4. Éxito
            await Swal.fire({
                icon: "success",
                title: "¡Solicitud enviada!",
                text: "Tu solicitud ha sido enviada y está pendiente de aprobación. Recibirás una notificación cuando sea revisada.",
                confirmButtonText: "Entendido",
                confirmButtonColor: "#10B981"
            });

            // Limpiar formulario
            setFormData({
                tipo: "",
                fechaInicio: "",
                fechaFin: "",
                motivo: ""
            });
            setDocumentos([]);
            setDiasCalculados(0);
            setSaldoVacaciones(null);
        } catch (error) {
            console.error("Error al enviar solicitud:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "No se pudo enviar la solicitud. Por favor intente nuevamente.",
                confirmButtonColor: "#EF4444"
            });
        } finally {
            setLoading(false);
            setUploading(false);
        }
    };

    const tipoSeleccionado = TIPOS_AUSENCIA.find(
        (t) => t.value === formData.tipo
    );

    return (
        <div
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0"
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                <Card
                    className="shadow-lg border-0"
                    style={{ borderRadius: "15px", overflow: "hidden" }}
                >
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(45deg, #2c3e50, #3498db)",
                            borderBottom: "3px solid #f39c12"
                        }}
                    >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <FaFileAlt className="me-3" />
                                Nueva Solicitud de Ausencia
                            </h2>
                            <p className="mb-0 opacity-75">
                                Complete el formulario para solicitar una ausencia
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        <Form onSubmit={handleSubmit}>
                            {/* Tipo de Ausencia */}
                            <Card className="shadow-sm border-0 mb-4">
                                <Card.Body className="p-4">
                                    <h5 className="fw-bold mb-3">
                                        <FaClipboardList className="me-2" />
                                        Tipo de Ausencia
                                    </h5>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">
                                            Seleccione el tipo de ausencia *
                                        </Form.Label>
                                        <Form.Select
                                            name="tipo"
                                            value={formData.tipo}
                                            onChange={handleInputChange}
                                            isInvalid={!!errores.tipo}
                                            className="form-control-lg"
                                            style={{ borderRadius: "10px" }}
                                            disabled={loading}
                                        >
                                            <option value="">-- Seleccione --</option>
                                            {TIPOS_AUSENCIA.map((tipo) => (
                                                <option key={tipo.value} value={tipo.value}>
                                                    {tipo.icon} {tipo.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {errores.tipo}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    {tipoSeleccionado && (
                                        <Alert
                                            variant="info"
                                            className="mb-0"
                                            style={{
                                                borderLeft: `4px solid ${tipoSeleccionado.color}`,
                                                borderRadius: "10px"
                                            }}
                                        >
                                            <div className="d-flex align-items-center">
                                                <span style={{ fontSize: "2em", marginRight: "15px" }}>
                                                    {tipoSeleccionado.icon}
                                                </span>
                                                <div>
                                                    <strong>{tipoSeleccionado.label}</strong>
                                                    {tipoSeleccionado.requiereDocumento && (
                                                        <p className="mb-0 mt-1">
                                                            <FaExclamationTriangle className="me-1 text-warning" />
                                                            Este tipo de ausencia requiere documentos adjuntos
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </Alert>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Fechas */}
                            <Card className="shadow-sm border-0 mb-4">
                                <Card.Body className="p-4">
                                    <h5 className="fw-bold mb-3">
                                        <FaCalendarAlt className="me-2" />
                                        Período de Ausencia
                                    </h5>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold">
                                                    Fecha de Inicio *
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="fechaInicio"
                                                    value={formData.fechaInicio}
                                                    onChange={handleInputChange}
                                                    isInvalid={!!errores.fechaInicio}
                                                    className="form-control-lg"
                                                    style={{ borderRadius: "10px" }}
                                                    disabled={loading}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errores.fechaInicio}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold">
                                                    Fecha de Fin *
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="fechaFin"
                                                    value={formData.fechaFin}
                                                    onChange={handleInputChange}
                                                    isInvalid={!!errores.fechaFin}
                                                    className="form-control-lg"
                                                    style={{ borderRadius: "10px" }}
                                                    disabled={loading}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errores.fechaFin}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {diasCalculados > 0 && (
                                        <Alert variant="success" className="mb-0">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span>
                                                    <FaCheckCircle className="me-2" />
                                                    Total de días solicitados:
                                                </span>
                                                <Badge bg="success" className="fs-5">
                                                    {diasCalculados} {diasCalculados === 1 ? "día" : "días"}
                                                </Badge>
                                            </div>
                                        </Alert>
                                    )}

                                    {formData.tipo === "Vacaciones" && (
                                        <Alert variant="info" className="mt-3 mb-0">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span>Saldo de vacaciones disponibles:</span>
                                                {loadingSaldo ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : (
                                                    <Badge
                                                        bg={
                                                            saldoVacaciones !== null &&
                                                                diasCalculados > saldoVacaciones
                                                                ? "danger"
                                                                : "primary"
                                                        }
                                                        className="fs-5"
                                                    >
                                                        {saldoVacaciones !== null
                                                            ? `${saldoVacaciones} días`
                                                            : "No disponible"}
                                                    </Badge>
                                                )}
                                            </div>
                                        </Alert>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Motivo */}
                            <Card className="shadow-sm border-0 mb-4">
                                <Card.Body className="p-4">
                                    <h5 className="fw-bold mb-3">Motivo / Observaciones</h5>

                                    <Form.Group>
                                        <Form.Label className="fw-semibold">
                                            Describa el motivo de su solicitud *
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            name="motivo"
                                            value={formData.motivo}
                                            onChange={handleInputChange}
                                            isInvalid={!!errores.motivo}
                                            placeholder="Ingrese el motivo de su ausencia (mínimo 10 caracteres)"
                                            style={{ borderRadius: "10px" }}
                                            disabled={loading}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errores.motivo}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            {formData.motivo.length} caracteres
                                        </Form.Text>
                                    </Form.Group>
                                </Card.Body>
                            </Card>

                            {/* Documentos Adjuntos */}
                            <Card className="shadow-sm border-0 mb-4">
                                <Card.Body className="p-4">
                                    <h5 className="fw-bold mb-3">
                                        <FaPaperclip className="me-2" />
                                        Documentos Adjuntos
                                        {tipoSeleccionado?.requiereDocumento && (
                                            <Badge bg="warning" text="dark" className="ms-2">
                                                Requerido
                                            </Badge>
                                        )}
                                    </h5>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">
                                            Adjuntar archivos (PDF, imágenes)
                                        </Form.Label>
                                        <Form.Control
                                            type="file"
                                            multiple
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                            isInvalid={!!errores.documentos}
                                            disabled={loading}
                                            style={{ borderRadius: "10px" }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errores.documentos}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            Máximo 5MB por archivo. Formatos: PDF, JPG, PNG
                                        </Form.Text>
                                    </Form.Group>

                                    {documentos.length > 0 && (
                                        <ListGroup>
                                            {documentos.map((doc, index) => (
                                                <ListGroup.Item
                                                    key={index}
                                                    className="d-flex justify-content-between align-items-center"
                                                >
                                                    <div className="d-flex align-items-center flex-grow-1">
                                                        <FaPaperclip className="me-2 text-primary" />
                                                        <span className="me-2">{doc.nombre}</span>
                                                        {doc.uploading && (
                                                            <Spinner animation="border" size="sm" />
                                                        )}
                                                        {doc.uploaded && (
                                                            <FaCheckCircle className="text-success" />
                                                        )}
                                                    </div>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => eliminarDocumento(index)}
                                                        disabled={loading || doc.uploading}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Botones de Acción */}
                            <div className="d-flex justify-content-end gap-3">
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    onClick={() => {
                                        setFormData({
                                            tipo: "",
                                            fechaInicio: "",
                                            fechaFin: "",
                                            motivo: ""
                                        });
                                        setDocumentos([]);
                                        setErrores({});
                                    }}
                                    disabled={loading}
                                    style={{ borderRadius: "10px" }}
                                >
                                    Limpiar Formulario
                                </Button>

                                <Button
                                    variant="primary"
                                    size="lg"
                                    type="submit"
                                    disabled={loading}
                                    style={{
                                        borderRadius: "10px",
                                        background: "linear-gradient(45deg, #3498db, #2980b9)",
                                        border: "none",
                                        minWidth: "200px"
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner
                                                animation="border"
                                                size="sm"
                                                className="me-2"
                                            />
                                            {uploading ? "Subiendo archivos..." : "Enviando..."}
                                        </>
                                    ) : (
                                        <>
                                            <FaCheckCircle className="me-2" />
                                            Enviar Solicitud
                                        </>
                                    )}
                                </Button>
                            </div>

                            {uploading && (
                                <div className="mt-3">
                                    <ProgressBar animated now={100} variant="primary" />
                                    <small className="text-muted">
                                        Subiendo documentos, por favor espere...
                                    </small>
                                </div>
                            )}
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
}

export default SolicitudAusencia;
